import React, { FC, useEffect, useContext, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { MtsCard, MtsCardBody, MtsLink } from '@mts-ds/components-react';

import { Dispatch, iRootState } from '../../store';
import { IAlert, INotificationClass, NotificationSeverity } from '../../models/types';

import NotificationIcon from '../../components_/Notification/NotificationIcon';

import Typography from '../../components_/Typography';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { paths } from '../../routes/Root/paths';
import { Space, Dropdown, Button } from 'antd';
import type { MenuProps } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import { ToastContext } from '../../components_/Toast';
import { isPostponeAvailable } from './utils';

const useStyles = makeStyles(({ getColor, spacing }) => ({
  body: {
    color: getColor('text-primary'),
    padding: spacing(2),
    position: 'relative'
  },
  info: {
    '& > *:not(:last-child)': {
      marginBottom: spacing(0.5)
    },
    hyphens: 'auto'
  },
  close: {
    position: 'absolute',
    right: 4,
    top: 4
  }
}));

const mapState = (state: iRootState) => ({
  alerts: state.alerts.list,
  admin: state.admin
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    confirmAlert: d.alerts.confirmAlert,
    deleteAlert: d.alerts.deleteAlert,
    addConfirmation: d.alerts.addConfirmation,
    postponeAlert: d.alerts.postponeAlert,
    // Навигация/фокус карты
    getMap: d.infrastructureMap.getMap,
    setFocus: d.infrastructureMap.setFocus,
    getOffice: d.infrastructureOffices.getOffice
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type IProps = {
  alert: IAlert;
  play: any;
  stop: any;
} & connectedProps;

const Item: FC<IProps> = ({
  alert,
  deleteAlert,
  addConfirmation,
  postponeAlert,
  confirmAlert,
  play,
  stop,
  getMap,
  setFocus,
  getOffice,
  admin
}) => {
  const { uid, type, title, description, isConfirm, severity, date, readTime, postponeTo } = alert;
  const classes = useStyles();
  const history = useHistory();
  const toast = useContext(ToastContext);
  const showError = (error: any, fallback: string) => {
    const typeStr = String(error?.response?.data?.type ?? '');
    const friendlyError =
      (typeStr.toLowerCase().includes('friendly') && ` ${error?.response?.data?.detail ?? ''}`) ||
      '';
    toast({
      text: friendlyError ? fallback + ': ' + friendlyError : fallback + '.',
      type: 'error'
    });
  };

  // Меню "Отложить" — секунды из /config/company.hazardTimer.delayTimerSeconds
  const delaySeconds: number[] = ((admin as any)?.hazardTimer?.delayTimerSeconds || []) as number[];
  const formatDelay = (s: number) => {
    const ld = moment.localeData();
    if (s % 60 === 0) {
      const m = s / 60;
      return ld.relativeTime(m, true, 'mm', false);
    }
    return ld.relativeTime(s, true, 'ss', false);
  };
  const postponeItems: MenuProps['items'] = delaySeconds.map((s: number) => ({
    key: String(s),
    label: formatDelay(s)
  }));
  // nowMs нужен, чтобы после дедлайна postponeTo кнопка скрылась без внешнего re-render
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (postponeTo == null) {
      return;
    }
    const postponeToTime = new Date(postponeTo).getTime();
    if (!Number.isFinite(postponeToTime)) {
      return;
    }
    const remaining = postponeToTime - Date.now();
    if (remaining <= 0) {
      setNowMs(Date.now());
      return;
    }
    const timer = window.setTimeout(() => setNowMs(Date.now()), remaining);
    return () => window.clearTimeout(timer);
  }, [postponeTo]);
  // delaySeconds — не API-контракт, а конфиг меню; без вариантов отложить нельзя
  const canPostpone = isPostponeAvailable({ postponeTo, readTime }, nowMs) && Boolean(delaySeconds.length);

  const onPostponeMenuClick: MenuProps['onClick'] = async ({ key }) => {
    const error = await postponeAlert({ id: uid, seconds: Number(key) });
    if (error) {
      showError(error, 'Ошибка при откладывании уведомления');
      return;
    }
    stop();
    deleteAlert(uid);
  };

  const handlePostponeDefault = () => {
    if (delaySeconds.length) {
      const def = delaySeconds[0];
      // эмулируем выбор первого пункта
      onPostponeMenuClick({ key: String(def) } as any);
    }
  };

  // Меню "Подтвердить" — варианты text[] из /config/company.notificationConfirmText, используем единый type READ
  const confirmList = ((admin as any)?.notificationConfirmText || []) as string[];
  const confirmItems: MenuProps['items'] = confirmList.map((text, idx) => ({
    key: String(idx),
    label: String(text)
  }));

  const onConfirmMenuClick: MenuProps['onClick'] = async ({ key }) => {
    const idx = Number(key);
    const text = confirmList[idx] ?? '';
    const error = await confirmAlert({
      uid,
      type: 'READ',
      text,
      isSuccessfull: true,
      time: new Date().toISOString(),
      userId: (alert as any).userId,
      userDevice: 'web',
      senderAddress: window.location.origin,
      senderMessageId: '',
      senderMessage: '',
      retryCount: 0
    });
    if (error) {
      showError(error, 'Ошибка подтверждения уведомления');
      return;
    }
    stop();
    deleteAlert(uid);
  };

  const handleConfirmDefault = async () => {
    const error = await confirmAlert({
      uid,
      type: 'READ',
      text: '',
      isSuccessfull: true,
      time: new Date().toISOString(),
      userId: (alert as any).userId,
      userDevice: 'web',
      senderAddress: window.location.origin,
      senderMessageId: '',
      senderMessage: '',
      retryCount: 0
    });
    if (error) {
      showError(error, 'Ошибка подтверждения уведомления');
      return;
    }
    stop();
    deleteAlert(uid);
  };


  const soundStart = async () => {
    if (severity === NotificationSeverity.Critical) {
      play(uid);
    }
  };

  const canNavigate =
    Boolean(alert?.officeId || alert?.placeId || alert?.mapId) &&
    alert?.lat !== null &&
    alert?.lat !== undefined &&
    alert?.lon !== null &&
    alert?.lon !== undefined;

  const handleNavigate = async () => {
    try {
      const officeId = (alert as any).officeId ?? (alert as any).placeId;
      const mapId = (alert as any).mapId;
      const lat = (alert as any).lat;
      const lon = (alert as any).lon;

      if (officeId) {
        localStorage.setItem('officeId', String(officeId));
        await getOffice(officeId);
      }

      if (mapId) {
        localStorage.setItem('mapId', String(mapId));
        await getMap(mapId);
      } else if (officeId) {
        localStorage.setItem('mapId', 'all');
        await getMap('all' as any);
      }

      history.push(paths.map.office.root);

      // Устанавливаем фокус на координаты события
      setFocus({ center: [lat, lon], zoom: 17 } as any);
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.error('Navigation from alert failed', e);
    }
  };

  useEffect(() => {
    soundStart();

    switch (severity) {
      case NotificationSeverity.Med:
        deleteAlert(uid);
        break;
      case NotificationSeverity.Low:
        deleteAlert(uid);
        break;
      case NotificationSeverity.High:
        deleteAlert(uid);
        break;
      case NotificationSeverity.Ignored:
        deleteAlert(uid);
        break;
    }

    const handler = setTimeout(() => {
      switch (severity) {
        case NotificationSeverity.Critical:
          addConfirmation(alert);
          break;
        default:
          deleteAlert(uid);
          break;
      }
    }, 10000);

    return () => {
      clearTimeout(handler);
    };
  }, []);

  return (
    <MtsCard>
      <MtsCardBody className={classes.body}>
        <Grid container spacing={3}>
          <Grid item xs={2}>
            <NotificationIcon notificationClass={type as INotificationClass} />
          </Grid>
          <Grid item xs={8} className={classes.info}>
            <Typography variant="p2Medium">{title}</Typography>
            <Typography variant="p2Regular">
              <div
                dangerouslySetInnerHTML={{
                  __html: moment(date).format('DD.MM.YYYY HH:mm:ss') + ' - ' + description
                }}
              />
            </Typography>
            {canNavigate && (
              <Typography variant="p2Regular">
                <MtsLink onClick={handleNavigate}>Перейти к месту</MtsLink>
              </Typography>
            )}
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              {canPostpone && (
                <Space.Compact>
                  <Button type="default" size="middle" onClick={handlePostponeDefault}>Отложить</Button>
                  <Dropdown
                    menu={{ items: postponeItems, onClick: onPostponeMenuClick }}
                    placement="bottomRight"
                    trigger={['click']}
                    getPopupContainer={() => document.body}
                  >
                    <Button type="default" size="middle" icon={<EllipsisOutlined />} />
                  </Dropdown>
                </Space.Compact>
              )}
              <Space.Compact>
                <Button type='primary' size="middle" onClick={handleConfirmDefault}>
                  Подтвердить
                </Button>
                {Boolean(confirmItems.length) && (
                  <Dropdown
                    menu={{ items: confirmItems, onClick: onConfirmMenuClick }}
                    placement="bottomRight"
                    trigger={['click']}
                    getPopupContainer={() => document.body}
                  >
                    <Button type='primary' size="middle" icon={<EllipsisOutlined />} />
                  </Dropdown>
                )}
              </Space.Compact>
            </div>
          </Grid>
          {/* {dateMap.map((stamp, index) => (
              <Grid key={index} container justifyContent="flex-end">
                <Typography variant="p3Regular">{stamp}</Typography>
              </Grid>
            ))} */}
        </Grid>
      </MtsCardBody>
    </MtsCard>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Item);
